/**
 * Cookie consent management.
 * Stores consent in localStorage and provides hooks for components
 * (notably Analytics) to react to consent changes.
 */

const KEY = "cleany_cookie_consent";
const VERSION = 1; // bump if categories or wording change → forces re-prompt

const EVENT = "cleany:consent-changed";

export const defaultConsent = {
    necessary: true,
    analytics: false,
};

export function getConsent() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed?.version !== VERSION) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function setConsent(choices) {
    const payload = {
        version: VERSION,
        timestamp: new Date().toISOString(),
        ...defaultConsent,
        ...choices,
        necessary: true, // always
    };
    try {
        localStorage.setItem(KEY, JSON.stringify(payload));
    } catch (e) {
        console.warn("consent: localStorage.setItem failed", e);
    }
    try {
        window.dispatchEvent(new CustomEvent(EVENT, { detail: payload }));
    } catch (e) {
        console.warn("consent: dispatchEvent failed", e);
    }
    return payload;
}

export function clearConsent() {
    try {
        localStorage.removeItem(KEY);
    } catch (e) {
        console.warn("consent: localStorage.removeItem failed", e);
    }
    try {
        window.dispatchEvent(new CustomEvent(EVENT, { detail: null }));
    } catch (e) {
        console.warn("consent: dispatchEvent failed", e);
    }
}

export function onConsentChange(handler) {
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
}

/**
 * Read the current marketing-consent boolean from the compact
 * `cleany_consent` localStorage key set by applyConsent().
 * Returns false when no consent has been given.
 */
export function readMarketingConsent() {
    try {
        const raw = localStorage.getItem("cleany_consent");
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        return !!parsed.marketing;
    } catch (e) {
        return false;
    }
}

/**
 * Read Meta's browser identifiers (_fbp, _fbc) from cookies.
 * Meta requires these to be passed through unhashed on the server-side
 * event so it can be matched back to the browser-side pixel fire.
 */
export function readMetaCookies() {
    if (typeof document === "undefined") return { fbp: null, fbc: null };
    const map = Object.fromEntries(
        document.cookie.split(";").map((c) => {
            const [k, ...v] = c.trim().split("=");
            return [k, v.join("=")];
        }),
    );
    return { fbp: map._fbp || null, fbc: map._fbc || null };
}

export const CONSENT_VERSION = VERSION;

/**
 * applyConsent — bridges the existing cookie banner to
 * Google Consent Mode v2 (via window.gtag), Microsoft Clarity
 * (via window.clarity), and the GTM dataLayer.
 *
 * Called from CookieConsent's three actions (Accept all / Reject all /
 * Save custom) and once at app-start for returning visitors.
 *
 * Writes a compact { statistics, marketing } payload to the
 * `cleany_consent` localStorage key so the pre-GTM head script in
 * public/index.html can hydrate the correct default state on the
 * next visit.
 */
export function applyConsent({ statistics, marketing }) {
    const state = { statistics: !!statistics, marketing: !!marketing };
    try {
        localStorage.setItem("cleany_consent", JSON.stringify(state));
    } catch (e) {
        console.warn("consent: applyConsent localStorage failed", e);
    }
    if (typeof window.gtag === "function") {
        window.gtag("consent", "update", {
            ad_storage: state.marketing ? "granted" : "denied",
            ad_user_data: state.marketing ? "granted" : "denied",
            ad_personalization: state.marketing ? "granted" : "denied",
            analytics_storage: state.statistics ? "granted" : "denied",
        });
    }
    if (typeof window.clarity === "function") {
        window.clarity("consentv2", {
            analytics_storage: state.statistics ? "granted" : "denied",
            ad_storage: state.marketing ? "granted" : "denied",
        });
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: "consent_update",
        consent_statistics: state.statistics,
        consent_marketing: state.marketing,
    });
}
